import { useState, useEffect } from "react";
import parse from 'html-react-parser';
import { Help, HelpItem, HelpItemTranslation, HelpLanguage } from "../interfaces/Help";
import { registerEventEnd, registerEventStart, replaceVideoShortcodes } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import { Video } from "../interfaces/Video";
import { useTranslation } from "react-i18next";
import { handleApiError, safeJsonParse } from "../../utils/api";

export interface HelpProps {
    id: string;
}

export default function HelpContent(props: HelpProps) {
    const { t, i18n } = useTranslation();
    
    const navigate = useNavigate();
    const [eventId, setEventId] = useState('');
    const [help, setHelp] = useState<Help>();
    const [show, setShow] = useState(false);
    const [showBody, setShowBody] = useState<boolean[]>([]);
    const [videos, setVideos] = useState<Video[]>();

    // Get translation for current language, fallback to default language
    const getTranslation = (item: HelpItem): HelpItemTranslation | null => {
        const currentLang = i18n.language as HelpLanguage;

        // Try to find translation for current language
        let translation = item.translations.find(t => t.lang === currentLang);

        // If not found, use default language
        if (!translation) {
            translation = item.translations.find(t => t.lang === item.defaultLang);
        }

        // If still not found, use first available translation
        if (!translation && item.translations.length > 0) {
            translation = item.translations[0];
        }

        return translation || null;
    };

    useEffect(() => {
        const currentLang = i18n.language;
        const requestOptionsGet = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/help/' + props.id + '?lang=' + currentLang, requestOptionsGet)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setHelp(data);
                let newShowBody = data.items.map((i, index) => {
                    return(false);
                });
                setShowBody(newShowBody);
            });
        fetch(import.meta.env.VITE_API_URL + '/v1/video-tutorials', requestOptionsGet)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setVideos(data);
            });
    }, []);

    const handleShow = (event: any) => {
        event.preventDefault();
        setShow(true);

        registerEventStart('help tooltip', props.id).then(data => { 
            setEventId(data);
        });
    }

    const handleHide = (event: any) => {
        event.preventDefault();
        registerEventEnd(eventId);
        setShow(false);
    }

    const handleShowBody = (i: number) => (event: any) => {
        event.preventDefault();             
        let newShowBody = showBody.map((e, index) => {
            if (index == i) {
                return !e;
            } else {
                return e;
            }
        });
        setShowBody(newShowBody);
    }

    return (
        <div className="help-container">
            <button className="toggle" onClick={ handleShow }></button>
            <div className={ 'help-box' + (show ? ' show' : '') }>
                <button className="close" onClick={ handleHide }></button>
                <div className="inner">
                    { help &&
                        help.items.map((i: HelpItem, index) => {
                            const trans = getTranslation(i);
                            if (!trans) return null;

                            return(
                                <div className="help-item"
                                    key={ 'help-item-' + props.id + '-' + index}>
                                    { trans.title && <h4>{ trans.title }</h4>}
                                    { trans.teaser &&
                                        <div className="teaser">
                                            { videos && parse(replaceVideoShortcodes(trans.teaser, videos)) }
                                        </div>
                                    }
                                    { !showBody[index] && (trans.body || trans.examples || trans.references) &&
                                        <div className="more-details">
                                            <a onClick={ handleShowBody(index) }>{ t("read more...") }</a>
                                        </div>
                                    }
                                    { showBody[index] && trans.body &&
                                        <div className={"body" + (showBody[index] ? ' show-body' : '')}>
                                            { videos && parse(replaceVideoShortcodes(trans.body, videos)) }
                                        </div>
                                    }
                                    { showBody[index] && trans.examples &&
                                        <div className="examples">
                                            { videos && parse(replaceVideoShortcodes(trans.examples, videos)) }
                                        </div>
                                    }
                                    { showBody[index] && trans.references &&
                                        <div className="references">
                                            { videos && parse(replaceVideoShortcodes(trans.references, videos)) }
                                        </div>
                                    }
                                </div>
                            )
                        })
                    }
                </div>
            </div>
        </div>
    );
}